import { Modal } from '#molecules';
import { useToast } from '#providers/ToastProvider';
import { produce } from 'immer';
import { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import { FileInfoList } from './FileInfoList';
import { PreSignedUrlRequestDto } from './types';
import { useUploadFiles } from './useFileUpload';

interface FileUploadProps {
  // 아이콘으로 들어갈 svg 컴포넌트
  children: ReactNode;
  onChange?: (selectedFiles: File[]) => void;
  maxCount: number;
  maxCountMsg: string;
  fileType: string;
  path: PreSignedUrlRequestDto['path'];
  onUploadFileComplete: (id: string, type: 'videos' | 'images') => void;
  onDeleteFileComplete: (id: string, type: 'videos' | 'images') => void;
  maxFileSize?: number;
  maxFileSizeMsg?: string;
  defaultFileInfoList?: {
    id: string;
    url: string;
    type: 'videos' | 'images';
  }[];
}

/**
 * 파일 업로드 컴포넌트
 * @param children 아이콘으로 들어갈 svg 컴포넌트
 * @param onChange 파일 업로드 시 실행될 함수
 * @param maxCount 최대 업로드 가능한 파일 개수
 * @param maxCountMsg 파일 업로드 제한 모달에 표시될 메시지
 * @param fileType 업로드 가능한 파일 타입
 * @param maxFileSize 업로드 가능한 파일 최대 크기
 * @param maxFileSizeMsg 파일 업로드 제한 모달에 표시될 메시지
 */
export const FileUpload = ({
  maxCount,
  maxCountMsg,
  children,
  fileType,
  // path,
  onUploadFileComplete,
  onDeleteFileComplete,
  maxFileSize = 300,
  maxFileSizeMsg = '파일은 최대 300mb까지 업로드할 수 있어요.',
  defaultFileInfoList: initialDefaultFileInfoList
}: FileUploadProps) => {
  const { showToast } = useToast();

  // 파일 업로드 제한 모달 표시 여부
  const [isMaxModalOpen, setIsMaxModalOpen] = useState(false);

  // 업로드한 파일 상세 모달 표시 여부
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // 업로드한 상세 파일
  const [detailFileInfo, setDetailFileInfo] = useState<{
    url: string;
    isVideo: boolean;
  }>({
    url: '',
    isVideo: false
  });

  const [defaultFileInfoList, setDefaultFileInfoList] = useState(
    initialDefaultFileInfoList
  );

  useEffect(() => {
    if (initialDefaultFileInfoList && initialDefaultFileInfoList.length > 0) {
      setDefaultFileInfoList(initialDefaultFileInfoList);
    } else {
      setDefaultFileInfoList([]);
    }
  }, [initialDefaultFileInfoList]);

  const {
    fileInfoList,
    setFileInfoList,
    uploadFiles: uploadFilesToS3
  } = useUploadFiles();

  const uploadFiles = async (filesArray: File[]) => {
    if (filesArray.length > 0) {
      uploadFilesToS3({
        selectedFiles: filesArray,
        path: 'exercise-image',
        onError: () => {
          showToast({
            type: 'fail',
            message: '이미지 업로드에 실패하였습니다.'
          });
        },
        onSuccess: ({ id, fileType }) => {
          onUploadFileComplete(id, fileType);
        }
      });
    }
  };

  // 파일이 없을 경우와 파일 업로드 제한일 경우 처리
  const handleOnChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;

    const filesArray = Array.from(selectedFiles);

    if (filesArray.length + fileInfoList.length > maxCount) {
      setIsMaxModalOpen(true);

      return;
    }

    if (
      maxFileSize &&
      filesArray.some(file => file.size > maxFileSize * 1024 * 1024)
    ) {
      setIsMaxModalOpen(true);
      return;
    }

    await uploadFiles(filesArray);

    e.target.value = '';
  };

  // 선택한 파일 제거
  // aws에서 사용하지 않는 파일들을 주기적으로 삭제함
  // 직접 aws에 업로드된 파일을 삭제할 필요 없음
  const onRemove = async (id: string, type: 'videos' | 'images') => {
    if (defaultFileInfoList && defaultFileInfoList.length > 0) {
      setDefaultFileInfoList(
        prev => prev?.filter(fileInfo => fileInfo.id !== id)
      );
    }

    setFileInfoList(
      produce(draft => draft.filter(fileInfo => fileInfo.id !== id))
    );

    onDeleteFileComplete(id, type);
  };

  return (
    <>
      <div className="flex gap-12">
        <div className="overflow-x-auto flex-1 pt-8 pr-8 flex gap-12">
          <div className="flex flex-col gap-8">
            <StyledLabel>
              <input
                style={{ display: 'none' }}
                type="file"
                accept={fileType}
                onChange={handleOnChange}
                multiple={true}
              />
              {children}
              <Text>
                {fileInfoList.length}/{maxCount}
              </Text>
            </StyledLabel>
          </div>
          {defaultFileInfoList &&
            defaultFileInfoList.length > 0 &&
            defaultFileInfoList.map((fileInfo, index) => {
              const { id, url, type } = fileInfo;

              const isVideo = type === 'videos';

              const onClick = () => {
                setDetailFileInfo({
                  url,
                  isVideo
                });

                setIsDetailModalOpen(true);
              };

              return (
                <div className="relative" key={id} onClick={onClick}>
                  <div
                    key={index}
                    className="w-120 h-120 rounded-lg overflow-hidden border-1 border-solid border-[#373245]"
                  >
                    {match(isVideo)
                      .with(true, () => (
                        <video
                          playsInline={true}
                          preload="metadata"
                          controls
                          className="w-full h-full object-contain"
                        >
                          <source src={url} type="video/mp4" />
                        </video>
                      ))
                      .with(false, () => (
                        <img
                          className="w-full h-full object-contain"
                          src={url}
                          alt="업로드한 이미지"
                        />
                      ))
                      .exhaustive()}
                  </div>

                  <StyledXButtonContainer
                    onClick={e => {
                      e.stopPropagation();
                      URL.revokeObjectURL(url);
                      onRemove(fileInfo.id, fileInfo.type);
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M13.1202 4.46004L11.7002 3.04004L8.1602 6.59004L4.6202 3.04004L3.2002 4.46004L6.7502 8.00004L3.2002 11.54L4.6202 12.96L8.1602 9.41004L11.7002 12.96L13.1202 11.54L9.5702 8.00004L13.1202 4.46004Z"
                        fill="#D7D3DE"
                      />
                    </svg>
                  </StyledXButtonContainer>
                </div>
              );
            })}
          {fileInfoList.length > 0 && (
            <FileInfoList
              fileInfoList={fileInfoList}
              setFileInfoList={setFileInfoList}
              onDeleteFileComplete={onDeleteFileComplete}
              setDetailFileInfo={setDetailFileInfo}
              setIsDetailModalOpen={setIsDetailModalOpen}
            />
          )}
        </div>
      </div>

      {isMaxModalOpen && (
        <Modal onClose={() => setIsMaxModalOpen(false)}>
          <div className="flex flex-col gap-28 w-full">
            <div className="flex flex-col gap-12">
              <div className="text-[#bcb8c3] text-center text-xl font-semibold font-['Pretendard Variable'] leading-normal tracking-tight">
                알림
              </div>
              <div className="flex flex-col gap-12">
                <div className="text-center text-[#d7d2de] text-base font-normal font-['Pretendard Variable'] leading-[21px]">
                  {maxCountMsg}
                </div>
                {maxFileSize && (
                  <div className="text-center text-[#d7d2de] text-base font-normal font-['Pretendard Variable'] leading-[21px]">
                    {maxFileSizeMsg}
                  </div>
                )}
              </div>
            </div>
            <div
              onClick={() => setIsMaxModalOpen(false)}
              className="h-[45px] px-10 py-3 bg-[#7750e9] rounded-lg justify-center items-center gap-2.5 inline-flex"
            >
              <div className="text-[#ebe9f4] text-base font-medium font-['Pretendard Variable'] leading-[21px]">
                닫기
              </div>
            </div>
          </div>
        </Modal>
      )}

      {isDetailModalOpen && !detailFileInfo.isVideo && (
        <Modal onClose={() => setIsDetailModalOpen(false)} transparent>
          {/* 추후 동영상도 렌더링해야할 경우 사용 */}
          {/* {detailFileInfo.isVideo ? (
            <VideoRenderer
              className="modal-video"
              controls={true}
              url={detailFileInfo.url}
            />
          ) : (
            <img className="modal-img" src={detailFileInfo.url} />
          )} */}
          <img className="modal-img" src={detailFileInfo.url} />
        </Modal>
      )}
    </>
  );
};

const StyledLabel = styled.label`
  display: flex;
  width: 120px;
  height: 120px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;

  border-radius: 12px;
  border: 1px solid var(--bg-500, #373245);
`;

const Text = styled.div`
  color: var(--Gray-Scale-700, #595463);

  /* NEW/body_03_regular */
  font-family: 'Pretendard Variable';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
`;

const StyledXButtonContainer = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  display: inline-flex;
  padding: 4px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  border-radius: 50%;
  background: var(--bg-400, #413d50);

  /* button drop S */
  box-shadow: 0px 0px 10px 0px rgba(34, 34, 34, 0.25);
`;
